/*
 * app-block-anchors.js
 *
 * Repairs `<a href="javascript:void(0)">` controls injected by third-party app
 * blocks — currently the Judge.me "all reviews" widget on the homepage.
 *
 * Why: those elements are buttons wearing a link's clothes. Crawlers see an
 * anchor whose href goes nowhere (Lighthouse's `crawlable-anchors` audit fails,
 * which is what keeps the homepage off a 100 SEO score), and assistive tech
 * announces "link" for something that does not navigate. The markup is owned by
 * the app, so it cannot be fixed in Liquid — this re-labels it in place.
 *
 * What it does, and deliberately does not do:
 *   - Drops the dead `href` and marks the element `role="button"`, which is what
 *     it actually is. Any click handler the app bound to the element (directly
 *     or by delegated selector) still fires; only the no-op navigation goes.
 *   - Restores keyboard operability that removing `href` would otherwise cost:
 *     `tabindex="0"` plus Enter/Space activation, matching native button
 *     behaviour.
 *   - Touches nothing else. Scoped to anchors with a `javascript:` href inside a
 *     known app-widget container, so ordinary theme links are never affected.
 *
 * App widgets render asynchronously and re-render on interaction, hence the
 * MutationObserver rather than a single pass on load.
 */
(function () {
  'use strict';

  var WIDGET_ROOTS = ['.jdgm-widget', '.jdgm-all-reviews-text'];
  var SELECTOR = WIDGET_ROOTS.map(function (root) {
    return root + ' a[href^="javascript:"]';
  }).join(', ');

  var FLAG = 'appBlockAnchorFixed';

  function activate(event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    event.preventDefault();
    event.currentTarget.click();
  }

  function repair(anchor) {
    if (anchor.dataset[FLAG]) return;
    anchor.dataset[FLAG] = 'true';

    anchor.removeAttribute('href');
    anchor.setAttribute('role', 'button');
    if (!anchor.hasAttribute('tabindex')) anchor.setAttribute('tabindex', '0');
    anchor.style.cursor = 'pointer';
    anchor.addEventListener('keydown', activate);
  }

  function sweep(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.matches && root.matches(SELECTOR)) repair(root);
    var found = root.querySelectorAll ? root.querySelectorAll(SELECTOR) : [];
    for (var i = 0; i < found.length; i++) repair(found[i]);
  }

  function start() {
    sweep(document.body);

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) sweep(added[j]);
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
